import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Button,
  Space,
  Row,
  Col,
  Drawer,
  Form,
  Input,
  Upload,
  Switch,
  message,
  Spin,
  Empty,
  Popconfirm,
  Image,
  Typography,
  Tag,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import dayjs from 'dayjs';

// ⬇️ StandardPage (il tuo componente wrapper)
import StandardPage from '../components/Layout/StandardPage';

// ⬇️ Service aggiornato (private /p/news, usa apiJson)
import {
  listNews,
  createNews,
  updateNews,
  deleteNews,
  type NewsDTO,
  type Page as PageResp,
} from '../services/news';

// ⬇️ Bearer dal context (già esistente nella tua app)
import { tokenManager, useAuth } from '../context/AuthContext';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

type NewsForm = {
  title: string;
  content?: string;
  imageUrl?: string | null;
  author: string;
  isPublished?: boolean;
};

const DEFAULT_PAGE_SIZE = 12;

/**
 * AuthImage: scarica una risorsa protetta (es. /api/p/news/image/...)
 * con Authorization Bearer e la mostra come blob URL.
 * Non crea file extra e si usa dove serve <Image>.
 */
const AuthImage: React.FC<{
  src?: string | null;
  alt?: string;
  style?: React.CSSProperties;
  className?: string;
  preview?: boolean;
  height?: number | string;
}> = ({ src, alt, style, className, preview = false, height }) => {
  const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let revoked: string | null = null;
    let abort = new AbortController();

    const load = async () => {
      if (!src) {
        setBlobUrl(undefined);
        return;
      }
      // se l'URL non è /api (es. cdn), non serve fetch con bearer
      if (!src.startsWith('/api/')) {
        setBlobUrl(src);
        return;
      }
      try {
        setLoading(true);
        const token = tokenManager.getAccess();
        const res = await fetch(src, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: abort.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        revoked = url;
      } catch (e) {
        setBlobUrl(undefined);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => {
      abort.abort();
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [src]);

  // Fallback skeleton semplice
  if (!blobUrl && loading) {
    return (
      <div
        className={className}
        style={{
          height: typeof height !== 'undefined' ? height : 200,
          background: '#f0f2f5',
          ...style,
        }}
      />
    );
  }

  if (!blobUrl) {
    return null;
  }

  return (
    <Image
      alt={alt}
      src={blobUrl}
      preview={preview}
      className={className}
      style={style}
    />
  );
};

const NewsManagement: React.FC = () => {
  const [data, setData] = useState<NewsDTO[]>([]);
  const [loading, setLoading] = useState(false);

  // Drawer / form
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsDTO | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [form] = Form.useForm<NewsForm>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Paginazione
  const [pageIndex, setPageIndex] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const fetchPage = async (page = pageIndex, size = pageSize) => {
    setLoading(true);
    try {
      const p: PageResp<NewsDTO> = await listNews({ page, size });
      setData(p.content ?? []);
      setTotal(p.totalElements ?? 0);
      setPageIndex(p.number ?? 0);
      setPageSize(p.size ?? size);
    } catch (error) {
      message.error('Errore nel caricamento delle news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(0, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangePage = async (page: number, size?: number) => {
    const nextIndex = page - 1; // AntD usa 1-based
    const nextSize = size ?? pageSize;
    await fetchPage(nextIndex, nextSize);
  };

  const openDrawer = (newsItem?: NewsDTO, view = false) => {
    setViewMode(view);
    if (newsItem) {
      setEditingNews(newsItem);
      form.setFieldsValue({
        title: newsItem.title,
        content: newsItem.content ?? '',
        author: newsItem.author,
        isPublished: newsItem.isPublished ?? true,
        imageUrl: newsItem.imageUrl ?? undefined,
      });
      if (newsItem.imageUrl) {
        // Mostriamo anteprima; per l’Upload è ok mostrare la “miniatura” fittizia;
        // l’editor userà l’AuthImage per vere preview se serve.
        setFileList([
          {
            uid: '-1',
            name: 'image',
            status: 'done',
            url: newsItem.imageUrl, // può essere /api/p/news/image/...
          },
        ]);
      } else {
        setFileList([]);
      }
    } else {
      setEditingNews(null);
      form.resetFields();
      form.setFieldsValue({ isPublished: true });
      setFileList([]);
    }
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setEditingNews(null);
    setViewMode(false);
    form.resetFields();
    setFileList([]);
  };

  const onSubmit = async (values: NewsForm) => {
    try {
      // Se upload fatto via antd, troverai la risposta nel fileList[0].response.url
      const imageUrl =
        fileList.length > 0
          ? ((fileList[0].url as string) ?? (fileList[0] as any)?.response?.url)
          : undefined;

      const payload = {
        title: values.title,
        content: values.content ?? '',
        imageUrl: imageUrl ?? null,
        author: values.author,
        isPublished: values.isPublished ?? true,
      };

      if (editingNews?.id) {
        await updateNews(editingNews.id, payload);
        message.success('News aggiornata con successo');
      } else {
        await createNews(payload);
        message.success('News creata con successo');
      }

      closeDrawer();
      fetchPage(pageIndex, pageSize);
    } catch (error) {
      message.error('Errore nel salvataggio della news');
    }
  };

  const onDelete = async (id: number) => {
    try {
      await deleteNews(id);
      message.success('News eliminata con successo');
      if (data.length === 1 && pageIndex > 0) {
        await fetchPage(pageIndex - 1, pageSize);
      } else {
        await fetchPage(pageIndex, pageSize);
      }
    } catch (error) {
      message.error("Errore nell'eliminazione della news");
    }
  };

  const onUploadChange = ({ fileList: fl }: { fileList: UploadFile[] }) => setFileList(fl);

  const { accessToken } = useAuth();
  
// sostituisci la tua useMemo con questa versione
const authHeaders = React.useMemo(() => {
  const h: Record<string, string> = {};
  if (accessToken) h.Authorization = `Bearer ${accessToken}`;
  return h;
}, [accessToken]);


const uploadProps: UploadProps = {
  name: 'file',
  action: '/api/p/news/image',
  headers: authHeaders,                 // ora è sempre Record<string,string>
  onChange: ({ fileList: fl }) => setFileList(fl as UploadFile[]),
  fileList,
  listType: 'picture-card',
};

  /* -------------------- ACTIONS in StandardPage -------------------- */
  const actions = [
    { label: 'Nuova News', type: 'primary' as const, icon: <PlusOutlined />, onClick: () => openDrawer() },
    { label: 'Ricarica',   type: 'default' as const, icon: <ReloadOutlined />, onClick: () => fetchPage(pageIndex, pageSize) },
  ];

  return (
    <StandardPage
      title="Gestione News"
      subtitle="Crea, modifica e gestisci le news del sito"
      actions={actions}
      loading={loading}
    >
      <Spin spinning={loading}>
        {data.length === 0 ? (
          <Empty description="Nessuna news presente" />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {data.map((item) => (
                <Col xs={24} sm={12} lg={8} xl={6} key={item.id}>
                  <Card
                    hoverable
                    cover={
                      item.imageUrl ? (
                        // Se l’URL è privato (/api/...), usa AuthImage per portare il Bearer,
                        // altrimenti usa <Image> normale
                        item.imageUrl.startsWith('/api/') ? (
                          <div style={{ height: 200, overflow: 'hidden' }}>
                            <AuthImage
                              alt={item.title}
                              src={item.imageUrl}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              height={200}
                              preview={false}
                            />
                          </div>
                        ) : (
                          <div style={{ height: 200, overflow: 'hidden' }}>
                            <Image
                              alt={item.title}
                              src={item.imageUrl}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              preview={false}
                            />
                          </div>
                        )
                      ) : undefined
                    }
                    actions={[
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => openDrawer(item, true)}
                        key="view"
                      />,
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => openDrawer(item, false)}
                        key="edit"
                      />,
                      <Popconfirm
                        title="Eliminare questa news?"
                        onConfirm={() => item.id && onDelete(item.id)}
                        okText="Sì"
                        cancelText="No"
                        key="del"
                      >
                        <Button type="text" icon={<DeleteOutlined />} danger />
                      </Popconfirm>,
                    ]}
                  >
                    <Card.Meta
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>{item.title}</span>
                          {!item.isPublished && <Tag color="orange">Bozza</Tag>}
                        </div>
                      }
                      description={
                        <div>
                          <Paragraph ellipsis={{ rows: 2 }}>{item.content}</Paragraph>
                          <Space size="small" style={{ marginTop: 8 }}>
                            <Text type="secondary">
                              <UserOutlined /> {item.author}
                            </Text>
                            {item.createdAt && (
                              <Text type="secondary">
                                <CalendarOutlined /> {dayjs(item.createdAt).format('DD/MM/YYYY')}
                              </Text>
                            )}
                          </Space>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                current={pageIndex + 1}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                pageSizeOptions={['8', '12', '24', '48', '96']}
                onChange={onChangePage}
              />
            </div>
          </>
        )}
      </Spin>

      <Drawer
        title={viewMode ? 'Visualizza News' : editingNews ? 'Modifica News' : 'Nuova News'}
        width={720}
        onClose={closeDrawer}
        open={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          !viewMode && (
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={closeDrawer}>Annulla</Button>
                <Button type="primary" onClick={() => form.submit()}>
                  {editingNews ? 'Aggiorna' : 'Crea'}
                </Button>
              </Space>
            </div>
          )
        }
      >
        <Form<NewsForm> form={form} layout="vertical" onFinish={onSubmit} disabled={viewMode}>
          <Form.Item name="title" label="Titolo" rules={[{ required: true, message: 'Inserisci il titolo' }]}>
            <Input placeholder="Titolo della news" />
          </Form.Item>

          <Form.Item name="content" label="Contenuto" rules={[{ required: true, message: 'Inserisci il contenuto' }]}>
            <TextArea rows={6} placeholder="Contenuto della news" />
          </Form.Item>

          <Form.Item name="author" label="Autore" rules={[{ required: true, message: "Inserisci l'autore" }]}>
            <Input placeholder="Nome dell'autore" />
          </Form.Item>

<Form.Item label="Immagine">
  <Upload {...uploadProps}>
    {fileList.length < 1 && (
      <div>
        <UploadOutlined />
        <div style={{ marginTop: 8 }}>Carica Immagine</div>
      </div>
    )}
  </Upload>
</Form.Item>

          <Form.Item name="isPublished" label="Pubblicata" valuePropName="checked" initialValue>
            <Switch />
          </Form.Item>
        </Form>

        {viewMode && editingNews && (
          <div style={{ marginTop: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {editingNews.createdAt && (
                <Text type="secondary">Creata il: {dayjs(editingNews.createdAt).format('DD/MM/YYYY HH:mm')}</Text>
              )}
              {editingNews.updatedAt && (
                <Text type="secondary">Ultima modifica: {dayjs(editingNews.updatedAt).format('DD/MM/YYYY HH:mm')}</Text>
              )}
            </Space>
          </div>
        )}
      </Drawer>
    </StandardPage>
  );
};

export default NewsManagement;
